import React, {useState} from 'react';
import {Box, Typography, Button} from '@mui/material';
import {MobileDateTimePicker} from '@mui/x-date-pickers/MobileDateTimePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import api from '../../../../api/axios';
import {toast} from 'react-toastify';
import BaseModal from '../../../utils/BaseModal';
import {Warning} from '@mui/icons-material';

const GlobalTradeCleanupModal = ({open, onClose, onSuccess}: any) => {
  // Default range: last 24 hours until now
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(24, 'hour'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  // Executes mass cleanup operation with type and MFA header
  const handleCleanup = async (type: 'CLOSE_OPEN' | 'REVERT_ALL_PENALTIES' | 'PERMIT_FREE_CANCEL', mfaCode?: string) => {
    try {
      await toast.promise(
          api.post(
              '/system/trades/global-cleanup',
              {
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString(),
                type
              },
              {
                headers: {
                  'X-MFA-Code': mfaCode
                }
              }
          ),
          {
            pending: 'Processing mass operation...',
            success: 'Mass operation successful',
            error: 'Operation failed'
          }
      );
      onSuccess();
      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  return (
      <BaseModal
          open={open}
          title="Global Cleanup Management"
          onAbort={onClose}
          maxWidth="md"
          mfaMode="REQUIRED"
      >
        {(mfaCode, isMfaValid) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-none">
                  <Warning className="text-red-500" fontSize="large" />
                  <div className="flex flex-col">
                    <span className="text-xs font-black text-red-500 uppercase tracking-tighter">Danger Zone</span>
                    <span className="text-[10px] text-red-400 leading-none">This affects multiple trades. Use with extreme caution during Steam outages.</span>
                  </div>
                </div>

                <Box className="flex flex-col gap-4">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col">
                      <Typography className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">
                        Start Date
                      </Typography>
                      <MobileDateTimePicker
                          value={startDate}
                          onChange={(newValue) => setStartDate(newValue)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              className: "bg-gray-50 dark:bg-neutral-800/50",
                              sx: {
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.05)', borderRadius: 0 },
                                '& .MuiInputBase-input': { color: 'white', fontSize: '12px', fontWeight: 'bold' }
                              }
                            }
                          }}
                      />
                    </div>

                    <div className="flex flex-col">
                      <Typography className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">
                        End Date (or Now)
                      </Typography>
                      <MobileDateTimePicker
                          value={endDate}
                          onChange={(newValue) => setEndDate(newValue)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              className: "bg-gray-50 dark:bg-neutral-800/50",
                              sx: {
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.05)', borderRadius: 0 },
                                '& .MuiInputBase-input': { color: 'white', fontSize: '12px', fontWeight: 'bold' }
                              }
                            }
                          }}
                      />
                    </div>
                  </div>
                  <p className="text-[9px] text-gray-600 mt-1 uppercase font-bold tracking-tight">
                    Ensure the selected range covers the target incident period.
                  </p>
                </Box>

                <div className="grid grid-cols-3 gap-3 mt-2">
                  <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      onClick={() => handleCleanup('CLOSE_OPEN', mfaCode)}
                      disabled={!isMfaValid}
                      className="!py-3 !text-gray-200 !font-semibold"
                      sx={{ borderRadius: 0 }}
                  >
                    Force-Close All
                  </Button>
                  <Button
                      fullWidth
                      variant="contained"
                      color="warning"
                      onClick={() => handleCleanup('REVERT_ALL_PENALTIES', mfaCode)}
                      disabled={!isMfaValid}
                      className="!py-3 !text-gray-200 !font-semibold"
                      sx={{ borderRadius: 0 }}
                  >
                    Revert Penalties
                  </Button>
                  <Button
                      fullWidth
                      variant="contained"
                      color="info"
                      onClick={() => handleCleanup('PERMIT_FREE_CANCEL', mfaCode)}
                      disabled={!isMfaValid}
                      className="!py-3 !text-gray-200 !font-semibold"
                      sx={{ borderRadius: 0 }}
                  >
                    Permit Free Cancel
                  </Button>
                </div>
              </div>
            </LocalizationProvider>
        )}
      </BaseModal>
  );
};

export default GlobalTradeCleanupModal;